import React from 'react';
import { ArrowsOutIcon } from '@phosphor-icons/react';
import { withBlockExtensions } from '@plone/volto/helpers';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';

import { Grade } from './Grade';
import { Lightbox } from './Lightbox';
import { Palco } from './Palco';
import { Trilha } from './Trilha';
import {
  anterior as indiceAnterior,
  contador,
  ESCALA_AMPLIADA,
  GALERIA_TITULO,
  getGaleriaImageProps,
  GRADE_COLUNAS_PADRAO,
  proxima as indiceProximo,
  toGaleriaFotos,
  type GaleriaData,
  type GaleriaFoto,
} from './helpers';

interface ViewProps {
  data: GaleriaData;
  block: string;
  isEditMode?: boolean;
  /** Presente só no editor: abre a foto de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}

/* `not-prose` desliga a tipografia de texto corrido que a página herda do
   editor (`[class*='__editor-content']`, em `base/_editor-content.scss`): o
   cartão traz a própria tipografia, e sem isto o `prose` recolore e
   redimensiona o que estiver dentro dele. Mesmo recurso das faixas da
   sub-home. */
const ROOT_CLASS = 'mpf-galeria not-prose';

/**
 * Pré-carrega as fotos vizinhas à do palco.
 *
 * Só no navegador e só depois da pintura: a ideia é que a seta responda na
 * hora, não disputar banda com a foto em cartaz. Sem estado nem limpeza — o
 * objetivo é apenas povoar o cache HTTP.
 */
const usePrecarregarVizinhas = (fotos: GaleriaFoto[], indice: number) => {
  React.useEffect(() => {
    if (typeof window === 'undefined' || fotos.length < 2) return;
    [indiceProximo(indice, fotos.length), indiceAnterior(indice, fotos.length)]
      .map((i) => getGaleriaImageProps(fotos[i], ESCALA_AMPLIADA))
      .forEach((props) => {
        if (props && 'src' in props) {
          const img = new window.Image();
          img.src = props.src;
        }
      });
  }, [fotos, indice]);
};

/**
 * Bloco "Galeria de imagens" (`mpfGaleriaImagens`).
 *
 * Um cartão com três estados: o palco (foto grande + trilha das próximas), a
 * grade "ver todas" — que substitui os dois no lugar, sem modal — e a foto
 * ampliada, essa sim num `<dialog>`.
 *
 * Diferente do `mpfSubHomeGaleria`, que é uma prateleira horizontal de cards:
 * aqui existe uma foto em cartaz, com legenda e crédito próprios, e o leitor
 * percorre a cobertura inteira.
 */
export const MpfGaleriaImagensBlockView: React.FC<ViewProps> = ({
  data,
  block,
  isEditMode,
  onEditItem,
}) => {
  const titleId = `galeria-imagens-${block}`;
  const fotos = React.useMemo(
    () => toGaleriaFotos(data?.items, data?.creditoPadrao),
    [data?.items, data?.creditoPadrao],
  );
  const total = fotos.length;

  const [indice, setIndice] = React.useState(0);
  const [modo, setModo] = React.useState<'palco' | 'grade'>('palco');
  const [ampliada, setAmpliada] = React.useState(false);

  // Tirar fotos no editor pode deixar o índice além do fim da lista.
  React.useEffect(() => {
    setIndice((atual) => (atual < total ? atual : 0));
  }, [total]);

  const foto = fotos[indice];
  usePrecarregarVizinhas(fotos, indice);

  /** Trilha: a miniatura promove a foto ao palco. */
  const irPara = React.useCallback((proximo: number) => {
    setIndice(proximo);
    setModo('palco');
  }, []);

  /**
   * Grade: a miniatura abre a foto ampliada direto, sem passar pelo palco.
   *
   * O modo continua em `grade` de propósito — quem fechar o lightbox volta
   * para a grade, que é de onde saiu, e não para um palco que não pediu.
   */
  const ampliarDaGrade = React.useCallback((proximo: number) => {
    setIndice(proximo);
    setAmpliada(true);
  }, []);

  const aoAnterior = React.useCallback(
    () => setIndice((atual) => indiceAnterior(atual, total)),
    [total],
  );
  const aoProximo = React.useCallback(
    () => setIndice((atual) => indiceProximo(atual, total)),
    [total],
  );

  const mostrarCredito = data?.mostrarCredito !== false;
  const titulo = data?.title || GALERIA_TITULO;

  if (total === 0) {
    return isEditMode ? (
      <section className={ROOT_CLASS}>
        <Cabecalho titulo={titulo} titleId={titleId} />
        <p className="mpf-galeria__placeholder">
          Escolha as fotos da galeria na barra lateral.
        </p>
      </section>
    ) : null;
  }

  return (
    <section aria-labelledby={titleId} className={ROOT_CLASS}>
      <Cabecalho
        titulo={titulo}
        titleId={titleId}
        contador={contador(indice, total)}
        acoes={
          <>
            <MPFButton
              variant="outline"
              size="m"
              onClick={() =>
                setModo((atual) => (atual === 'grade' ? 'palco' : 'grade'))
              }
            >
              {modo === 'grade' ? 'Voltar ao palco' : `Ver todas (${total})`}
            </MPFButton>
            <MPFButton
              variant="outline"
              size="m"
              onClick={() => setAmpliada(true)}
            >
              <ArrowsOutIcon aria-hidden="true" />
              Ampliar
            </MPFButton>
          </>
        }
      />

      {modo === 'palco' ? (
        <>
          <div className="mpf-galeria__corpo">
            <Palco
              foto={foto}
              total={total}
              onAnterior={aoAnterior}
              onProxima={aoProximo}
              onAmpliar={() => setAmpliada(true)}
              acoes={
                onEditItem && (
                  <EditItemButton
                    label={`Editar a foto ${foto.numero}`}
                    onClick={() => onEditItem(foto.sourceIndex)}
                    className="mpf-galeria__lapis"
                  />
                )
              }
            />
            <Trilha
              fotos={fotos}
              indice={indice}
              onSelecionar={irPara}
              onVerTodas={() => setModo('grade')}
            />
          </div>

          {mostrarCredito && foto.credito && (
            <p className="mpf-galeria__credito">{foto.credito}</p>
          )}
        </>
      ) : (
        <Grade
          fotos={fotos}
          indice={indice}
          colunas={data?.colunasGrade ?? GRADE_COLUNAS_PADRAO}
          onSelecionar={ampliarDaGrade}
        />
      )}

      {/* Discreta de propósito: quem enxerga já vê a foto trocar; quem usa
          leitor de tela precisa ser avisado de que o clique fez efeito. */}
      <p aria-live="polite" className="sr-only">
        {`Foto ${foto.numero} de ${total}${foto.legenda ? `. ${foto.legenda}` : ''}`}
      </p>

      <Lightbox
        aberto={ampliada}
        fotos={fotos}
        indice={indice}
        mostrarCredito={mostrarCredito}
        onFechar={() => setAmpliada(false)}
        onAnterior={aoAnterior}
        onProxima={aoProximo}
        onSelecionar={setIndice}
      />
    </section>
  );
};

const Cabecalho: React.FC<{
  titulo: string;
  titleId: string;
  contador?: string;
  acoes?: React.ReactNode;
}> = ({ titulo, titleId, contador: textoContador, acoes }) => (
  <header className="mpf-galeria__cabecalho">
    <span aria-hidden="true" className="mpf-galeria__marca" />
    <h2 id={titleId} className="mpf-galeria__titulo">
      {titulo}
    </h2>
    {textoContador && (
      <span aria-hidden="true" className="mpf-galeria__pilula">
        {textoContador}
      </span>
    )}
    {acoes && <div className="mpf-galeria__acoes">{acoes}</div>}
  </header>
);

export default withBlockExtensions(MpfGaleriaImagensBlockView);
